import {makeAutoObservable, observable, runInAction} from "mobx";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IRankingsApiProvider} from "data/providers/api/rankings.api.provider";
import {IRosterItem} from "data/types/roster";
import {IRankingsPayload} from "data/types/rankings";
import {Empty} from "data/types/global";

export interface IRoundRankingsPayload extends IRankingsPayload {
	roundId: number;
}

export interface IRoundRankingsSummaryPayload {
	roundId: number;
}

export interface IRoundRankingsSummaryResponse {
	roundSummary: IContestSummary[];
}

export interface IContestSummary {
	contestId: number;
	roster: IRosterItem;
	ranking: IRankingRoundSummary;
}

export interface IRankingRoundSummary {
	points: number;
	rank: number;
}

export interface IRoundSummaryStore {
	get roundSummary(): IContestSummary[];

	getSummaryByContestId(contestId: number): Empty<IContestSummary>;
	fetchSummary(roundId: number): Promise<void>;
}

@injectable()
export class RoundSummaryStore implements IRoundSummaryStore {
	@observable _roundSummary: IContestSummary[] = [];
	constructor(@inject(Bindings.RankingsApiProvider) private _apiProvider: IRankingsApiProvider) {
		makeAutoObservable(this);
	}

	get roundSummary() {
		return this._roundSummary;
	}

	getSummaryByContestId(contestId: number): Empty<IContestSummary> {
		return this._roundSummary.find((it) => it.contestId === contestId);
	}

	async fetchSummary(roundId: number) {
		const response = await this._apiProvider.roundSummary({roundId});
		runInAction(() => {
			this._roundSummary = response.data.success.roundSummary;
		});
	}
}
